/**
 * Phase 45 Plan 45-05 — GroupByToggle (Customer / Owner / None).
 * Phase 50 Plan 50-08 — Extended with 'tier' option (MO-MULTI-01).
 *
 * Interactive segment control per UI-SPEC §Surface 1 GroupByToggle. Active
 * state visibly distinct (`bg-ms-surface text-ms-text-strong shadow-sm`).
 *
 * Keyboard nav: each button is focusable; arrow keys move focus through the
 * group, Tab leaves the group. Per UI-SPEC §A11y Contract / Focus rings.
 * Keyboard nav uses OPTIONS.length arithmetic — auto-adjusts to 4 options.
 */
import { useRef } from 'react';

export type GroupBy = 'customer' | 'owner' | 'tier' | 'none';

const OPTIONS: Array<{ value: GroupBy; label: string }> = [
  { value: 'customer', label: 'Customer' },
  { value: 'owner', label: 'Owner' },
  { value: 'tier', label: 'Tier' },
  { value: 'none', label: 'None' },
];

export interface GroupByToggleProps {
  value: GroupBy;
  onChange: (v: GroupBy) => void;
}

export function GroupByToggle({ value, onChange }: GroupByToggleProps) {
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);

  function handleKey(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % OPTIONS.length;
      buttonsRef.current[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + OPTIONS.length) % OPTIONS.length;
      buttonsRef.current[prev]?.focus();
    }
  }

  return (
    <div
      className="inline-flex items-center gap-2 ml-auto"
      role="group"
      aria-label="Group by"
    >
      <span className="text-[11.5px] text-ms-text-muted">Group by</span>
      <div className="inline-flex bg-ms-surface-2 rounded-md p-0.5 border border-ms-border-subtle">
        {OPTIONS.map((opt, idx) => {
          const active = opt.value === value;
          const cls = active
            ? 'bg-ms-surface text-ms-text-strong shadow-sm'
            : 'text-ms-text-secondary hover:text-ms-text-strong';
          return (
            <button
              key={opt.value}
              ref={(el) => {
                buttonsRef.current[idx] = el;
              }}
              type="button"
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ms-orange/40 ${cls}`}
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              onKeyDown={(e) => handleKey(e, idx)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
